import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import "./style.css";
import CreateLifeUpdateForm from "../createLifeUpdateForm";

const lifeEventCategories = [
  {
    id: "work",
    name: "Work",
    icon: "💼",
  },
  {
    id: "education",
    name: "Education",
    icon: "🎓",
  },
  {
    id: "relationship",
    name: "Relationship",
    icon: "❤️",
  },
  {
    id: "home",
    name: "Home and living",
    icon: "🏠",
  },
  {
    id: "family",
    name: "Family",
    icon: "👨‍👩‍👧‍👦",
  },
  {
    id: "travel",
    name: "Travel",
    icon: "🌍",
  },
  {
    id: "interests",
    name: "Interests and activities",
    icon: "⭐",
  },
  {
    id: "health",
    name: "Health and wellbeing",
    icon: "💚",
  },
  {
    id: "milestones",
    name: "Milestones and achievements",
    icon: "🏆",
  },
  {
    id: "remembrance",
    name: "Remembrance",
    icon: "🕯️",
  },
  {
    id: "custom",
    name: "Create your own",
    icon: "🚩",
  },
];

// Mapping of categories to their related life events
const lifeEventsByCategory = {
  work: ["New Job", "Promotion", "Left Job", "Retirement", "Work Anniversary", "Started New Project"],
  education: ["Graduated", "Started School", "Got a Degree", "Finished Course", "Achieved Certification", "Enrolled in Program"],
  relationship: ["Started Relationship", "Got Engaged", "Got Married", "In a Relationship", "It's Complicated", "Single", "Divorced"],
  home: ["Moved", "Bought a Home", "Sold a Home", "Renovated", "New Roommate", "New Pet"],
  family: ["New Family Member", "Family Member's Birthday", "Family Reunion", "Family Milestone", "Family Celebration"],
  travel: ["New City", "New Country", "Vacation", "Backpacking", "Road Trip", "Adventure"],
  interests: ["New Hobby", "Achievement", "Competition", "Event", "New Skill", "Milestone"],
  health: ["Fitness Goal", "Health Milestone", "Wellness Achievement", "Recovery", "Health Update"],
  milestones: ["Personal Achievement", "Career Milestone", "Life Milestone", "Award", "Recognition", "Accomplishment"],
  remembrance: ["Memorial", "Anniversary", "Tribute", "In Memory", "Remembrance"],
  custom: [],
};

export default function CreateLifeUpdateModal({ onClose, onCategorySelect, onLifeEventSelect, user, dispatch, getAllPosts }) {
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [customTitle, setCustomTitle] = useState("");
  const [showCustomInput, setShowCustomInput] = useState(false);
  const [selectedLifeEvent, setSelectedLifeEvent] = useState(null);
  const stepRef = useRef("categories");
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  // Keep step in a ref so mobile/browser back always sees the latest screen.
  useEffect(() => {
    if (selectedLifeEvent) {
      stepRef.current = "form";
    } else if (showCustomInput) {
      stepRef.current = "custom";
    } else if (selectedCategory) {
      stepRef.current = "events";
    } else {
      stepRef.current = "categories";
    }
  }, [selectedLifeEvent, showCustomInput, selectedCategory]);

  // Lock background scroll while the modal is open (important on mobile).
  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, []);

  const handleCategoryClick = (category) => {
    if (category.id === "custom") {
      // For "Create your own", go straight to custom input
      setSelectedCategory(category);
      setShowCustomInput(true);
    } else {
      setSelectedCategory(category);
    }
  };

  const handleBackToCategories = () => {
    setSelectedCategory(null);
    setShowCustomInput(false);
    setCustomTitle("");
  };

  const handleLifeEventClick = (lifeEvent) => {
    setSelectedLifeEvent({
      category: selectedCategory,
      lifeEvent: lifeEvent,
      isCustom: false,
    });
  };

  const handleCustomTitleSubmit = () => {
    if (customTitle.trim()) {
      setSelectedLifeEvent({
        category: selectedCategory,
        lifeEvent: customTitle.trim(),
        isCustom: true,
      });
      setShowCustomInput(false);
    }
  };

  const handleBackFromForm = () => {
    const wasCustom = selectedLifeEvent?.isCustom;
    setSelectedLifeEvent(null);
    if (wasCustom) {
      setShowCustomInput(true);
    }
  };

  // Mobile/browser back should step through the modal
  // (form → events/custom → categories → close) instead of leaving the page.
  useEffect(() => {
    window.history.pushState({ lifeUpdateModal: true }, "");

    const onPopState = () => {
      const step = stepRef.current;

      if (step === "form") {
        setSelectedLifeEvent((current) => {
          if (current?.isCustom) {
            setShowCustomInput(true);
          }
          return null;
        });
        window.history.pushState({ lifeUpdateModal: true }, "");
        return;
      }

      if (step === "events" || step === "custom") {
        setSelectedCategory(null);
        setShowCustomInput(false);
        setCustomTitle("");
        window.history.pushState({ lifeUpdateModal: true }, "");
        return;
      }

      onCloseRef.current?.();
    };

    window.addEventListener("popstate", onPopState);
    return () => {
      window.removeEventListener("popstate", onPopState);
    };
  }, []);

  const currentLifeEvents = selectedCategory ? lifeEventsByCategory[selectedCategory.id] || [] : [];

  // If a life event is selected, show the form
  if (selectedLifeEvent) {
    return (
      <CreateLifeUpdateForm
        user={user}
        lifeEventData={selectedLifeEvent}
        onClose={onClose}
        onBack={handleBackFromForm}
        dispatch={dispatch}
        getAllPosts={getAllPosts}
      />
    );
  }

  const modal = (
    <div className="create_life_update_overlay" onClick={onClose}>
      <div className="create_life_update_modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="create_life_update_header">
          <button
            onClick={selectedCategory ? handleBackToCategories : onClose}
            className="back_btn"
            aria-label={selectedCategory ? "Back to categories" : "Close"}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path
                d="M15 18L9 12L15 6"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
          <h2>{selectedCategory ? selectedCategory.name : "Create life update"}</h2>
          <div style={{ width: "24px" }}></div> {/* Spacer for centering */}
        </div>

        {/* Illustrative Banner - only show on category selection */}
        {!selectedCategory && (
          <div className="life_update_banner">
            <div className="banner_illustration">
              <div className="banner_item balloon">🎈</div>
              <div className="banner_item cake">🎂</div>
              <div className="banner_item fireworks">🎆</div>
              <div className="banner_item flag">🚩</div>
              <div className="banner_item airplane">✈️</div>
              <div className="banner_item trophy">🏆</div>
            </div>
          </div>
        )}

        {/* Main Content */}
        <div className="create_life_update_content">
          {!selectedCategory ? (
            // Category Selection View
            <>
              <div className="life_events_section">
                <h3 className="life_events_title">Life events</h3>
                <p className="life_events_subtitle">
                  Share and remember important moments from your life.
                </p>
              </div>

              <div className="category_selection">
                <h4 className="category_prompt">SELECT A CATEGORY</h4>
                <div className="category_grid">
                  {lifeEventCategories.map((category) => (
                    <button
                      key={category.id}
                      className="category_item"
                      onClick={() => handleCategoryClick(category)}
                    >
                      <div className="category_icon">{category.icon}</div>
                      <span className="category_label">{category.name}</span>
                    </button>
                  ))}
                </div>
              </div>
            </>
          ) : showCustomInput ? (
            // Custom Title Input View
            <div className="life_event_selection">
              <div className="life_events_section">
                <h3 className="life_events_title">Create your own</h3>
                <p className="life_events_subtitle">
                  Enter a custom title for your life event.
                </p>
              </div>

              <div className="custom_title_input_wrapper">
                <input
                  type="text"
                  className="custom_title_input"
                  placeholder="Enter event title (e.g., Got my driver's license)"
                  value={customTitle}
                  onChange={(e) => setCustomTitle(e.target.value)}
                  maxLength={100}
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && customTitle.trim()) {
                      handleCustomTitleSubmit();
                    }
                  }}
                />
                <div className="char_count_custom">{customTitle.length}/100</div>
              </div>

              <div className="life_event_actions">
                <button
                  className="life_event_cancel_btn"
                  onClick={handleBackToCategories}
                >
                  Cancel
                </button>
                <button
                  className="life_event_continue_btn"
                  onClick={handleCustomTitleSubmit}
                  disabled={!customTitle.trim()}
                >
                  Continue
                </button>
              </div>
            </div>
          ) : (
            // Life Event Selection View
            <div className="life_event_selection">
              <div className="life_events_section">
                <h3 className="life_events_title">Select an event</h3>
                <p className="life_events_subtitle">
                  Choose a specific life event or create your own.
                </p>
              </div>

              <div className="life_event_list">
                {currentLifeEvents.map((lifeEvent, index) => (
                  <button
                    key={index}
                    className="life_event_item"
                    onClick={() => handleLifeEventClick(lifeEvent)}
                  >
                    <span className="life_event_name">{lifeEvent}</span>
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      className="life_event_arrow"
                    >
                      <path
                        d="M9 18L15 12L9 6"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  // Portal to body so mobile profile stacking (profile_top z-index 100 vs
  // profile_bottom z-index 1) cannot trap the overlay under the header.
  return createPortal(modal, document.body);
}
